import React, { useEffect, useState } from 'react';
import Dialog from '../../../../../dialog/Dialog';
import { Alert, Chip, Divider, Skeleton, Typography } from '@mui/material';
import ButtonStartIcon from '../../../../../button/ButtonStartIcon';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import DialogAttachMedia from '../../../../../dialog/DialogAttachMedia';
import ChipMedia from '../../../../../chip/ChipMedia';
import useEvidenceOfLearning from '../../../../../../hooks/maestro/useEvidenceOfLearning';
import { validate } from '../../../../../../utils';
import { handlerErrorV2 } from '../../../../../../utils/errors';
import useUpload from '../../../../../../hooks/upload/useUpload';
import { getEvidenceOfLearningByLcIdAndLevelId } from '../../../../../../services/maestro/evidence-of-learning.service';
import { useParams } from 'react-router-dom';
import TextEditor from '../../../../../TextEditor/TextEditor';
import ReadOnlyTextEditor from '../../../../../TextEditor/ReadOnlyTextEditor';
import DialogConfirmCancel from '../../../../../dialog/DialogConfirm';

const DialogEvidenceOfLearning = ({ onClose, open, formData, type }) => {
  const types = ['create', 'edit', 'delete', 'create-comment', 'edit-comment', 'delete-comment'];
  const { curriculumId } = useParams();
  const [form, setForm] = useState(formData);
  const [errors, setError] = useState(null);
  const [mode, setMode] = useState('');
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const student = useSelector((state) => state?.classMarking?.student);
  const permissions = useSelector((state) => state?.session?.permissions?.['marking-class'] || {});
  const completed = useSelector((state) => state?.classMarking.completedType);

  const currentMode = useSelector((state) =>
    state?.classMarking?.mode?.find((item) => student?._id === item.studentId)
  );
  const alertSave = useSelector((state) => state?.alert?.save || {});

  const { deleteFiles, uploadFile } = useUpload();

  const { createEvidenceOfLearningByLcIdAndLevelId, deleteEvidenceOfLearning } = useEvidenceOfLearning();

  const onMode = (value) => {
    if (isEmpty(value)) {
      setMode('');
    } else {
      setMode('editing');
    }
  };

  const onConfirm = async (e) => {
    e.preventDefault();

    if (currentMode && alertSave.mode && alertSave.onSave) {
      alertSave.onSave();
    }

    if (!types.includes(type)) {
      setError({ message: 'Invalid type' });
      return;
    }
    const fieldRules = {
      comment: 'required|string'
    };
    if (form.comment === '<p><br></p>') {
      form.comment = '';
    }
    const errors = validate(fieldRules, form);
    if (!isEmpty(errors)) {
      const message = errors.message || errors.comment || 'Some fields are required or invalid';
      setError({ ...errors, message });
    } else {
      setLoading(true);
      try {
        let result = { error: false };
        if (['create', 'edit', 'create-comment', 'edit-comment'].includes(type)) {
          const media = form.media
            ?.filter((item) => item.type === 'file' && item.file)
            ?.map((item) => ({
              ...item,
              id: item._id || item.id,
              type: item.mimetype
            }));

          if (media?.length > 0) {
            const resultUpload = await uploadFile(media, 'uploads');
            if (resultUpload.error) {
              setError(resultUpload);
              return;
            }
            const newMedia = resultUpload.data;
            form.media = form.media
              .map((item) => {
                if (item.type === 'file') {
                  const find = newMedia.find((media) => media.id === item.id);
                  if (find) {
                    return { ...item, ...find };
                  }
                }

                return item;
              })
              .filter((item) => item.link);
          }
        }
        if (['delete-comment', 'delete'].includes(type)) {
          const media = form.media.filter((item) => item.type === 'file');

          if (media.length > 0) {
            const resultDelete = await deleteFiles(media, 'uploads');
            if (resultDelete.error) {
              setError(resultDelete);
              return;
            }
          }
        }

        if (['create', 'edit'].includes(type)) {
          result = await createEvidenceOfLearningByLcIdAndLevelId(
            student._id,
            formData.lcId,
            formData.levelId,
            form
          );
        }
        if (type === 'delete') {
          result = await deleteEvidenceOfLearning(student._id, form._id);
        }

        // if (type === 'create-comment') {
        //   result = await createEvidenceOfLearningForGranularStep(curriculumId, id, form.lcId, form.gsId, form)
        // }

        // if (type === 'edit-comment') {
        //   result = await updateEvidenceOfLearningForGranularStep(curriculumId, id, form.lcId, form.gsId, form._id, form)
        // }

        // if (type === 'delete-comment') {
        //   result = await deleteEvidenceOfLearningForGranularStep(curriculumId, id, form.lcId, form.gsId, form._id)
        // }

        if (result.error) {
          setError(result);
        } else {
          onClose();
        }
      } catch (error) {
        const message = handlerErrorV2(error);
        setError({ message });
      } finally {
        setLoading(false);
      }
    }
  };

  const onSaveMedia = async (type, data) => {
    const currentMedia = form.media || [];
    if (type === 'add') {
      setForm({
        ...form,
        media: [
          ...currentMedia,
          {
            id: data._id || new Date().getTime(),
            ...data
          }
        ]
      });
    } else {
      const media = currentMedia
        .map((item) => ({ id: item._id || item.id, ...item }))
        .filter((item) => item.id !== data.id);
      setForm({ ...form, media });
      if (data.type === 'file') {
        const result = await deleteFiles([data], 'uploads');
        if (result.error) {
          setError(result);
        }
      }
    }
  };

  const onChange = (key, value) => {
    setForm({ ...form, [key]: value });
    onMode(value);
    if (key === 'comment') {
      setError({ ...errors, [key]: null, message: null });
    } else {
      setError({ ...errors, [key]: null });
    }
  };

  const onDeleteEvidence = async (id) => {
    setIsLoadingDelete(true);
    try {
      const result = await deleteEvidenceOfLearning(student._id, id);
      if (result.error) {
        setError(result);
      } else {
        onClose();
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      setError({ error: true, message });
    } finally {
      setIsLoadingDelete(false);
    }
  };

  const onLoad = async () => {
    setIsLoading(true);
    try {
      const response = await getEvidenceOfLearningByLcIdAndLevelId(
        curriculumId,
        student._id,
        formData.lcId,
        formData.levelId
      );
      if (!response.data && response.error) {
        setForm({ ...formData, comment: '' });
      } else {
        setForm(response.data);
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      if (message === 'Evidence of learning not found') {
        setForm({ ...formData, comment: '' });
      } else {
        setError({ error: true, message });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      onLoad();
    }
  }, [open]);

  const commentIsEmpty =
    !form?.comment ||
    form?.comment === '<p><br></p>' ||
    form?.comment === '<p></p>' ||
    form?.comment === '<p><br></p><p></p>';

  const Actions = (
    <div className='w-full flex gap-4 justify-between'>
      <div className='flex gap-4 justify-end items-center'>
        <ButtonStartIcon
          onClick={() => setModal({ form: {}, type: 'add' })}
          color='primary'
          variant='text'
          disabled={loading || commentIsEmpty}
          startIcon={'link'}
        >
          Attach media
        </ButtonStartIcon>
        {mode && (
          <>
            <Divider orientation='vertical' flexItem />
            <Chip
              label={mode}
              color={mode === 'editing' ? 'primary' : 'inherit'}
              className='capitalize font-bold'
            />
          </>
        )}
      </div>
      <div className='flex gap-4 justify-end'>
        {permissions['by-student'] && form?._id && (
          <>
            <ButtonStartIcon
              color='error'
              variant='contained'
              onClick={() => setConfirmDelete(true)}
              disabled={loading || isLoadingDelete}
              loading={isLoadingDelete}
              startIcon={['fal', 'check']}
            >
              delete
            </ButtonStartIcon>
            <div className='px-2' />
          </>
        )}
        <ButtonStartIcon
          onClick={onClose}
          color='primary'
          variant='outlined'
          disabled={loading}
          startIcon={['fal', 'times']}
        >
          Cancel
        </ButtonStartIcon>
        {permissions['by-student'] && (
          <ButtonStartIcon
            onClick={onConfirm}
            color='primary'
            variant='contained'
            disabled={loading || commentIsEmpty}
            loading={loading}
            startIcon={['fal', 'check']}
          >
            Save
          </ButtonStartIcon>
        )}
      </div>
    </div>
  );
  return (
    <>
      <Dialog
        id='evidence-of-learning'
        show={open}
        title={`Evidence of learning`}
        onCloseHeader={permissions['by-student'] && completed === 'completedStudent' ? false : true}
        maxWidth={'md'}
        actions={permissions['by-student'] && completed === 'completedStudent' ? Actions : null}
        onClose={onClose}
      >
        {isLoading ? (
          <>
            <div className='h-screen-1/2'>
              <Skeleton height={'10%'} variant='rounded' />
              <Skeleton height={'90%'} variant='rounded' className='mt-2' />
            </div>
          </>
        ) : (
          <div className='flex flex-col gap-2'>
            {permissions['by-student'] && completed === 'completedStudent' ? (
              <TextEditor
                content={form.comment}
                setContent={(value) => onChange('comment', value)}
                isEditable={permissions['by-student'] && completed === 'completedStudent' ? true : false}
                showMenuBar={permissions['by-student'] && completed === 'completedStudent' ? true : false}
                placeholder='The student has not entered any information yet'
                // editorVariant={editorVariant}
                // error={error}
              />
            ) : (
              <ReadOnlyTextEditor content={form.comment} />
            )}

            {form.media && form.media.length > 0 && (
              <div className='flex flex-wrap gap-4 items-center'>
                {form.media.map((data, index) => {
                  return (
                    <ChipMedia
                      key={`media-${index}-${data._id}-${data.type}`}
                      index={index}
                      data={data}
                      disabled={loading}
                      onDelete={
                        permissions['by-student'] && completed === 'completedStudent'
                          ? () =>
                              setModal({
                                type: 'delete',
                                form: {
                                  id: data._id || data.id,
                                  ...data
                                }
                              })
                          : undefined
                      }
                    />
                  );
                })}
              </div>
            )}
            {errors && errors.message && (
              <Alert severity='error' onClose={() => setInputErrors({ message: null })}>
                {errors.message}
              </Alert>
            )}
          </div>
        )}
      </Dialog>
      {modal && (
        <DialogAttachMedia
          open={!!modal}
          onClose={() => setModal(false)}
          formData={modal.form}
          onSave={(data) => onSaveMedia(modal.type, data)}
          type={modal.type}
        />
      )}
      {confirmDelete && (
        <DialogConfirmCancel
          id='confirm-delete-evidence'
          open={confirmDelete}
          loading={loading}
          onCancel={() => setConfirmDelete(false)}
          textCancel='Cancel'
          onConfirm={() => {
            onDeleteEvidence(form._id);
            setConfirmDelete(false);
          }}
          title='Delete evidence of learning'
          maxWidth='xs'
          onCloseHeader={() => setConfirmDelete(false)}
          showActions={true}
        >
          <Typography variant='h6' className='mb-2'>
            Are you sure you want to delete this evidence of learning?
          </Typography>
          <p className='text-sm opacity-75'>
            This action cannot be undone. All media and comments associated with this evidence of learning
            will be permanently deleted.
          </p>
        </DialogConfirmCancel>
      )}
    </>
  );
};

export default DialogEvidenceOfLearning;
